// El servicio del turno del TENS y la plantilla de registro que le toca
// (plan del mundo TENS, PR2 y PR5, 2026-10-04).
//
// El servicio usa el MISMO catálogo que `profiles.servicio` (SERVICIO_OPTIONS de
// perfilCatalogos.js): mismo concepto, mismo nombre, en Mi perfil y acá. De ese
// servicio sale una plantilla: qué dispositivos se verifican, qué cuidados se
// ofrecen en la ronda, qué motivos de "no realizable" hay y cómo se arma la
// evolución.
//
// Hay DOS plantillas. La de UCI (`uci`) es la plantilla UPCA de Bupa tal cual, con
// las MISMAS referencias de tens.js (no copias: así un test puede probar con `toBe`
// que no existe un catálogo paralelo). La de sala (`sala`) sirve a los demás
// servicios: parte de la plantilla de UCI (de donde heredan sus campos y sus frases) y
// le suma lo que los textos de las fuentes de sala dicen que registra un TENS (ver
// tensCatalogoSala.js, que declara qué está respaldado y qué es una adaptación de MiPAE). `sala` es un BORRADOR: la urgencia, la recuperación, la
// pediatría, la atención primaria y el pabellón caen ahí porque es la plantilla
// general, y ninguna fuente respalda que calcen del todo (pregunta abierta, ver el
// plan). Nunca se inventó una plantilla por servicio sin fuente.
//
// Un registro sin servicio se lee como UCI. Es el adapter de siempre (datos
// viejos se LEEN, jamás se migran): para el TENS de hoy "igual que antes" ES
// la plantilla UPCA.
import {
  DISPOSITIVOS_VISITA, CHEQUEOS_ENTORNO, CUIDADOS_TENS, MOTIVOS_NO_REALIZABLE,
  EVOLUCION_TENS, GRUPOS_EVOLUCION_TENS,
} from './tens';
import {
  MEDIDAS_CAIDAS_SALA, OPCIONES_SEGURIDAD_SALA, CAMPO_INFORMA,
  OPCIONES_DISPOSITIVOS_EXTRA_SALA, OPCIONES_EDUCACION_EXTRA_SALA, OPCIONES_OBSERVACION_EXTRA_SALA,
  CUIDADOS_SALA_EXTRA, MOTIVOS_SALA_EXTRA,
} from './tensCatalogoSala';
import { SERVICIO_OPTIONS, SERVICIOS_VALIDOS } from './perfilCatalogos';
import { tensServiciosHabilitado } from './tensPerfil';

// ── La plantilla de sala se arma DESDE la de UCI: un campo igual es el mismo objeto ──
const campoUci = (id) => EVOLUCION_TENS.find((c) => c.id === id);
// El mismo campo con otras opciones (misma etiqueta y mismo id).
const conOpciones = (id, opciones, extra = {}) => ({ ...campoUci(id), ...extra, opciones });
const opcionesUci = (id, { sin = [], mas = [] } = {}) =>
  [...campoUci(id).opciones.filter((o) => !sin.includes(o.id)), ...mas];

// Sala no lleva drogas vasoactivas ni ventilación mecánica (son de UCI). Lleva la VVP,
// que en UCI no se pregunta en la evolución.
// Invasivos por servicio (2026-10-10, pedido del fundador: "hacer lo que hicimos con MiPAE
// en turno enfermero"): fuera de UCI el TET y la línea arterial no se borran, quedan tras
// "Más opciones" (mismo criterio que `contextoEvolucion.js` para Médico-quirúrgico y
// Cirugía). Un dispositivo ya marcado se muestra siempre. Hasta hoy sala los quitaba del
// todo, y un paciente de sala con línea arterial no tenía dónde registrarse.
const DISPOSITIVOS_SALA = ['cvc', 'vvp', 'cup', 'sng', 'sny', 'tqt', 'drenajes', 'ostomias', 'tet', 'linea_arterial'];
const DISPOSITIVOS_SECUNDARIOS_SALA = ['tet', 'linea_arterial'];
const DISPOSITIVOS_VISITA_SECUNDARIOS_SALA = ['tet', 'la'];

/**
 * Reparte las opciones en las que van a la vista y las que quedan tras "Más opciones".
 * Lo que ya tiene dato va SIEMPRE a la vista: cambiar de servicio jamás esconde un hecho.
 * @param {string[]} ids las opciones, en su orden
 * @param {string[]} secundarias las que el servicio deja un toque más lejos
 * @param {string[]} enUso las que ya tienen dato
 */
export function repartirOpciones(ids = [], secundarias = [], enUso = []) {
  const sec = new Set(secundarias || []);
  const uso = new Set(enUso || []);
  return {
    principales: ids.filter((id) => !sec.has(id) || uso.has(id)),
    secundarias: ids.filter((id) => sec.has(id) && !uso.has(id)),
  };
}

export const CAMPOS_SALA = [
  campoUci('condiciones'),
  campoUci('conciencia'),
  campoUci('conducta'),
  // "Monitorización continua" y "En unidad con" son de una cama de UCI (2026-10-10, pedido
  // del fundador): sala dice "Se mantiene con" y no ofrece la monitorización.
  conOpciones('soporte', opcionesUci('soporte', { sin: ['vm', 'monitorizacion'] }), { label: 'Se mantiene con' }),
  campoUci('piel'),
  conOpciones('dispositivos', DISPOSITIVOS_SALA
    .map((id) => [...campoUci('dispositivos').opciones, ...OPCIONES_DISPOSITIVOS_EXTRA_SALA].find((o) => o.id === id)),
  { secundarias: DISPOSITIVOS_SECUNDARIOS_SALA }),
  campoUci('sitio_insercion'),
  campoUci('regimen'),
  campoUci('tolerancia'),
  campoUci('diuresis'),
  campoUci('deposiciones'),
  conOpciones('seguridad', OPCIONES_SEGURIDAD_SALA, { label: 'Medidas de caídas que ejecuté y seguridad' }),
  campoUci('pertenencias'),
  campoUci('pertenencias_gestion'),
  campoUci('educacion_a'),
  conOpciones('educacion_tema', opcionesUci('educacion_tema', { mas: OPCIONES_EDUCACION_EXTRA_SALA })),
  campoUci('educacion_resultado'),
  CAMPO_INFORMA,
  conOpciones('observacion', opcionesUci('observacion', { mas: OPCIONES_OBSERVACION_EXTRA_SALA })),
];

// El array ES el orden de la pantalla y de la prosa. Los ids `piel`, `dispositivos`,
// `alimentacion` y `educacion` se conservan para que `gruposCubiertosPorVisita` siga
// funcionando sin tocarla. Un quirk que ya existía en UCI y se documenta sin
// arreglarlo: las pertenencias viven dentro del grupo `seguridad`, así que su "ya
// viene de tu visita" no se marca (el compilador sí lo maneja).
// `fuente: 'signos'` es un grupo sin campos que se llena con las tomas de signos de las
// rondas: la pantalla lo muestra resuelto, no lo pregunta de nuevo.
export const GRUPOS_SALA = [
  { id: 'estado', label: 'Estado del paciente', campos: ['condiciones', 'conciencia', 'conducta', 'soporte'] },
  { id: 'signos', label: 'Signos vitales', campos: [], fuente: 'signos' },
  { id: 'piel', label: 'Piel', campos: ['piel'] },
  { id: 'dispositivos', label: 'Dispositivos', campos: ['dispositivos', 'sitio_insercion'] },
  { id: 'alimentacion', label: 'Alimentación', campos: ['regimen', 'tolerancia'] },
  { id: 'eliminacion', label: 'Eliminación', campos: ['diuresis', 'deposiciones'] },
  { id: 'seguridad', label: 'Caídas, seguridad y pertenencias', campos: ['seguridad', 'pertenencias', 'pertenencias_gestion'] },
  { id: 'educacion', label: 'Educación', campos: ['educacion_a', 'educacion_tema', 'educacion_resultado'] },
  {
    id: 'avisos',
    label: 'Avisé a la enfermera',
    campos: ['informa'],
    nota: 'Lo que avisas de los signos vitales, de una lesión de piel o del sitio de inserción se marca donde lo registras, para que el texto no lo diga dos veces.',
  },
  { id: 'observaciones', label: 'Observaciones', campos: ['observacion'] },
];

export const CUIDADOS_SALA = [...CUIDADOS_TENS, ...CUIDADOS_SALA_EXTRA];

// Los motivos de UCI que también valen en sala (misma referencia) más los de sala. Los
// dos de inestabilidad (hemodinámica y respiratoria) quedan fuera: son de la UCI.
const MOTIVOS_DE_UCI_EN_SALA = ['procedimiento_en_curso', 'negativa_paciente'];
export const MOTIVOS_SALA = [
  MOTIVOS_SALA_EXTRA[0],
  ...MOTIVOS_NO_REALIZABLE.filter((m) => MOTIVOS_DE_UCI_EN_SALA.includes(m.id)),
  ...MOTIVOS_SALA_EXTRA.slice(1),
];

export const PLANTILLAS_TENS = {
  uci: {
    id: 'uci',
    nombre: 'Cuidados intensivos e intermedios',
    estado: 'referencia',
    dispositivos: DISPOSITIVOS_VISITA.map((d) => d.id),
    entorno: CHEQUEOS_ENTORNO.map((c) => c.id),
    cuidadosRonda: CUIDADOS_TENS,
    motivosNoRealizable: MOTIVOS_NO_REALIZABLE,
    campos: EVOLUCION_TENS,
    grupos: GRUPOS_EVOLUCION_TENS,
  },
  sala: {
    id: 'sala',
    nombre: 'Sala (medicina, cirugía, urgencia, recuperación y otros)',
    estado: 'borrador',
    // Se OBSERVAN y se avisa: instalar, cambiar o retirar un dispositivo no es parte del registro.
    dispositivos: DISPOSITIVOS_VISITA.map((d) => d.id),
    dispositivosSecundarios: DISPOSITIVOS_VISITA_SECUNDARIOS_SALA,
    entorno: CHEQUEOS_ENTORNO.map((c) => c.id).filter((id) => ['lineas_ordenadas', 'unidad_ordenada'].includes(id)),
    cuidadosRonda: CUIDADOS_SALA,
    motivosNoRealizable: MOTIVOS_SALA,
    campos: CAMPOS_SALA,
    grupos: GRUPOS_SALA,
    // Qué opciones de `seguridad` cuentan como medidas de prevención de caídas.
    medidasCaidas: MEDIDAS_CAIDAS_SALA.map((m) => m.id),
  },
};

// A qué plantilla corresponde cada servicio. Los seis que no son UCI apuntan a
// 'sala' (un borrador general: ver la cabecera).
const PLANTILLA_POR_SERVICIO = {
  uci_uti: 'uci',
  urgencia: 'sala',
  medico_quirurgico: 'sala',
  pabellon: 'sala',
  aps: 'sala',
  neonatologia_pediatria: 'sala',
  otro: 'sala',
};

// Una entrada por cada servicio del perfil, en el mismo orden y con las mismas
// etiquetas (un test fija que no sobra ni falta ninguno).
export const SERVICIOS_TENS = SERVICIO_OPTIONS.map((o) => ({
  id: o.value,
  label: o.label,
  plantilla: PLANTILLA_POR_SERVICIO[o.value],
}));

/**
 * El servicio de este turno: el que la persona eligió en el hub, si no el de su
 * perfil, si no ninguno (null). Un valor fuera del catálogo cuenta como ausente.
 * Con el kill-switch apagado todo el mundo es UCI.
 * @param {object|null} estado el estado del turno TENS
 * @param {object|null} profile el perfil REAL
 * @returns {string|null}
 */
export function servicioDe(estado, profile) {
  if (!tensServiciosHabilitado()) return 'uci_uti';
  if (SERVICIOS_VALIDOS.includes(estado?.servicio)) return estado.servicio;
  if (SERVICIOS_VALIDOS.includes(profile?.servicio)) return profile.servicio;
  return null;
}

/**
 * La plantilla de un servicio. Sin servicio, o con uno que no es del catálogo, la
 * de UCI (adapter: lo registrado antes de esta ola compila igual).
 * @param {string|null|undefined} servicioId
 */
export function plantillaDe(servicioId) {
  return PLANTILLAS_TENS[PLANTILLA_POR_SERVICIO[servicioId]] || PLANTILLAS_TENS.uci;
}

// ── Nada registrado se esconde al cambiar de servicio ────────────────────────
// Si el servicio cambia a mitad de turno (o se eligió mal al empezar), lo ya registrado
// con la otra plantilla sigue contándose en el documento. Para que la persona pueda
// verlo y corregirlo, la pantalla suma como opciones lo registrado que la plantilla
// vigente no ofrece. Sin nada registrado de más devuelven las MISMAS referencias.
const TODAS_LAS_PLANTILLAS = Object.values(PLANTILLAS_TENS);

/** Los cuidados de la ronda de la plantilla, más los ya marcados que ofrece otra. */
export function cuidadosConRegistrados(plantilla, cuidados = {}) {
  const ofrecidos = plantilla.cuidadosRonda;
  const extra = [];
  TODAS_LAS_PLANTILLAS.forEach((p) => p.cuidadosRonda.forEach((c) => {
    if (cuidados?.[c.id] && !ofrecidos.some((o) => o.id === c.id) && !extra.some((e) => e.id === c.id)) extra.push(c);
  }));
  return extra.length ? [...ofrecidos, ...extra] : ofrecidos;
}

const idsDe = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/**
 * Los campos y grupos de la evolución de la plantilla, más lo ya registrado que otra
 * plantilla pidió: una opción marcada que esta no ofrece se suma a su campo, y un campo
 * entero que esta no tiene (las drogas vasoactivas en sala, los avisos en UCI) va en un
 * grupo aparte. No toca los valores: solo hace visible lo que ya cuenta el documento.
 */
export function camposConRegistrados(plantilla, valores = {}) {
  const otros = TODAS_LAS_PLANTILLAS.filter((p) => p !== plantilla);
  const opcionDeOtra = (campoId, opId) => otros
    .map((p) => p.campos.find((c) => c.id === campoId)?.opciones.find((o) => o.id === opId))
    .find(Boolean) || null;

  let cambio = false;
  const campos = plantilla.campos.map((c) => {
    const faltan = idsDe(valores?.[c.id])
      .filter((id) => !c.opciones.some((o) => o.id === id))
      .map((id) => opcionDeOtra(c.id, id)).filter(Boolean);
    if (!faltan.length) return c;
    cambio = true;
    return { ...c, opciones: [...c.opciones, ...faltan] };
  });

  const ausentes = [];
  otros.forEach((p) => p.campos.forEach((c) => {
    if (plantilla.campos.some((x) => x.id === c.id) || ausentes.some((x) => x.id === c.id)) return;
    const registrados = idsDe(valores?.[c.id]);
    if (!registrados.length) return;
    const opciones = c.opciones.filter((o) => registrados.includes(o.id));
    if (opciones.length) ausentes.push({ ...c, opciones: c.opciones });
  }));

  if (!cambio && !ausentes.length) return { campos: plantilla.campos, grupos: plantilla.grupos };
  return {
    campos: [...campos, ...ausentes],
    grupos: ausentes.length
      ? [...plantilla.grupos, { id: 'otro_servicio', label: 'Registrado con otro servicio', campos: ausentes.map((c) => c.id) }]
      : plantilla.grupos,
  };
}
